import {
    Avatar,
    Box,
    Drawer,
    IconButton,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { store } from '../redux/store/index';
import * as patientActions from '../redux/actions/patientActions';
import { useSelector } from "react-redux";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useToast } from "../context/ToastContext";
import { calculateBmi } from "../utils/bmiCalculator";
import EditIcon from '@mui/icons-material/Edit';
import moment from "moment";
import AddIcon from '@mui/icons-material/Add';
import { API } from "../api/api";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from '@mui/icons-material/Close';
import PatientConditionTimeline from "../components/PatientConditionTimeline";
import PatientInfo from "./PatientInfo";
import { socket } from "../socket/socket";
import { formatWeeks } from "../utils/formatWeekAndDays";

const StatCard = ({ unit, value, label, edit, handleChangeMedicalDetails, medicalDetails, handleUpdatePatientData, validateMedicalFields, getDobFromAge, showToast, setIsEditingMedicalInfo, patientId, patient, lmpDate, setLmpDate, inputRef, saveEdd }) => (
    <Paper
        elevation={0}
        sx={{
            px: 0.5,
            py: 0.3,
            borderRadius: 2,
            minWidth: 30,
            textAlign: "center",
            border: "1px solid #E6E8EC",
        }}
    >
        {
            edit ? label == "HT" ? <>
                <TextField
                    style={{
                        width: '34px'
                    }}
                    value={value || ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        const parsedValue = value === '' ? '' : parseInt(value, 10);
                        if (!isNaN(parsedValue)) {
                            handleChangeMedicalDetails("height", parsedValue);
                        } else {
                            handleChangeMedicalDetails("height", '');
                        }
                    }}
                    placeholder="000"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: 'none',
                            },
                            '& input': {
                                fontSize: { xl: "12px", lg: '12px', md: '10px' },
                                height: '10px',
                                padding: '4px 1px',
                                backgroundColor: '#DDEEFD',
                                textAlign: 'center',
                                borderRadius: 5,
                                opacity: 1
                            },
                        },
                    }}
                />
                {<CheckCircleOutlineIcon fontSize='small' sx={{ cursor: 'pointer' }} onClick={() => {
                    const { height } = medicalDetails;
                    if (height == null || height === "" || height < 86.36 || height > 332.74) {
                        showToast("Please enter a height between 86.36 and 332.74 cm", "error");
                        return;
                    }
                    store.dispatch(patientActions.updatePatientDetailsRequestAction(patientId, { height: parseFloat(height) }));
                    if (setIsEditingMedicalInfo) setIsEditingMedicalInfo(false);
                }} />}
            </>
                :
                label == "WT" ? <>
                    <TextField
                        style={{
                            width: '34px'
                        }}
                        value={value || ''}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                                handleChangeMedicalDetails("weight", '');
                                return;
                            }
                            if (/^\d*\.?\d*$/.test(value)) {
                                handleChangeMedicalDetails("weight", value);
                            }
                        }}
                        placeholder="00.0"
                        inputProps={{ maxLength: 5 }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    border: 'none',
                                },
                                '& input': {
                                    fontSize: { xl: "12px", lg: '12px', md: '10px' },
                                    height: '10px',
                                    padding: '4px 1px',
                                    backgroundColor: '#DDEEFD',
                                    textAlign: 'center',
                                    borderRadius: 5
                                },
                            },
                        }}
                    />
                    {<CheckCircleOutlineIcon fontSize='small' sx={{ cursor: 'pointer' }} onClick={() => {
                        const { weight } = medicalDetails;
                        if (weight == null || weight === "" || isNaN(parseFloat(weight)) || parseFloat(weight) < 23 || parseFloat(weight) > 140) {
                            showToast("Please enter a weight between 23 and 140 kg", "error");
                            return;
                        }
                        store.dispatch(patientActions.updatePatientDetailsRequestAction(patientId, { weight: parseFloat(weight) }));
                        if (setIsEditingMedicalInfo) setIsEditingMedicalInfo(false);
                    }} />}
                </>
                    :
                    label == "Age" ?
                        <>
                            <TextField
                                style={{
                                    width: '34px'
                                }}
                                value={value || ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^\d*$/.test(value)) {
                                        handleChangeMedicalDetails("age", value);
                                    }
                            
                                }}
                                placeholder="XXX"
                                inputProps={{
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                    maxLength: 3,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '& input': {
                                            fontSize: { xl: "12px", lg: '12px', md: '10px' },
                                            height: '10px',
                                            padding: '4px 1px',
                                            backgroundColor: '#DDEEFD',
                                            textAlign: 'center',
                                            borderRadius: 5
                                        },
                                    },
                                }}
                            />
                            {<CheckCircleOutlineIcon
                                fontSize="small"
                                sx={{ cursor: "pointer", }}
                                onClick={() => {
                                    const { age } = medicalDetails;
                                    if (age == null || age === "") {
                                        showToast("Please enter an age between 18 and 80 yrs", "error");
                                        return;
                                    }

                                    if (age < 18 || age > 80) {
                                        showToast("Please enter an age between 18 and 80 yrs", "error");
                                        return;
                                    }
                                    const dob = getDobFromAge(age);
                                    if (setIsEditingMedicalInfo) setIsEditingMedicalInfo(false);
                                    store.dispatch(patientActions.updatePatientDetailsRequestAction(patientId, { dateOfBirth: dob }))
                                }}
                            />}
                        </> :
                        label == "EDD BY USG" &&
                        <>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <Box
                                    onClick={() => inputRef.current && inputRef.current.showPicker()}
                                    sx={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <TextField
                                        type="date"
                                        inputRef={inputRef}
                                        fullWidth
                                        value={
                                            lmpDate
                                                ? moment(lmpDate).format('YYYY-MM-DD')
                                                : patient?.eddByUsg ? moment(patient?.eddByUsg).format('YYYY-MM-DD') : ''
                                        }
                                        onChange={(e) =>
                                            setLmpDate(e.target.value)
                                        }
                                        inputProps={{
                                            min: moment().subtract(1, 'year').format('YYYY-MM-DD'),
                                            max: moment().add(1, 'year').format('YYYY-MM-DD'),
                                            style: {
                                                height: '20px',
                                                padding: '0px 0px',
                                                fontWeight: 700,
                                                fontSize: 13,
                                                color: "#1F2937",
                                            },

                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                '& fieldset': {
                                                    border: 'none',
                                                },
                                            },
                                            '& input': {
                                                border: 'none',
                                                cursor: 'pointer',
                                                backgroundColor: '#DDEEFD',
                                                padding: '4px 1px',
                                                borderRadius: 5,
                                                textAlign: 'center'
                                            },

                                            // Hide calendar icon
                                            "& input::-webkit-calendar-picker-indicator": {
                                                opacity: 0,
                                                width: 0,
                                                position: "absolute",
                                            },
                                        }}
                                    />
                                </Box>

                                <CheckCircleOutlineIcon
                                    fontSize="small"
                                    sx={{
                                        cursor: "pointer",
                                    }}
                                    onClick={() => {
                                        if (!lmpDate) {
                                            showToast("Please enter valid date", "error");
                                            return;
                                        }
                                        if (setIsEditingMedicalInfo) setIsEditingMedicalInfo(false);
                                        // API Call
                                        saveEdd()
                                    }}
                                />

                            </Box>
                        </>

                :
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: "#1F2937",
                    }}
                >
                    {value}
                </Typography>}

        <Typography
            sx={{
                fontSize: 9,
                color: "#6B7280",
            }}
        >
            {label} {unit && "(" + unit + ")"}
        </Typography>
    </Paper>
);

const StatWkCard = ({ value,label }) => (
    <Paper
        elevation={0}
        sx={{
            px: 0.5,
            py: 0.3,
            borderRadius: 2,
            minWidth: 30,
            textAlign: "center",
            border: "1px solid #E6E8EC",
        }}
    >
        <Typography
            sx={{
                fontWeight: 700,
                fontSize: 12,
                color: "#1F2937",
                textAlign: 'center'

            }}
        >
            {value}
        </Typography>

        <Typography
            sx={{
                fontSize: 9,
                color: "#6B7280",
            }}
        >
            {label}
        </Typography>
    </Paper>
);

export default function PatientHeader() {
    const { patientId, consultationId } = useParams();
    const patientReducer = useSelector(state => state.patientReducer);
    const patient = patientReducer?.patientInfo
    const [medicalDetails, setMedicalDetails] = useState({})
    const [isEditingMedicalInfo, setIsEditingMedicalInfo] = useState(false);
    const showToast = useToast()
    const patientHistory = patientReducer?.patientInfo
    const [showPartnerDetails, setShowPartnerDetails] = useState(false);
    const [isPartnerSaved, setIsPartnerSaved] = useState(false);
    const [partnerDetails, setPartnerDetails] = useState({
        name: "",
        age: "",
        height: "",
        weight: "",
        bmi: ""
    });

    const [partnerChanged, setPartnerChanged] = useState(false);
    const [isEditingPartnerInfo, setIsEditingPartnerInfo] = useState(false);
    const [patientDrawerOpen, setPatientDrawerOpen] = useState(false);
    const location = useLocation();
    const route = location.pathname.split("/").pop().toLowerCase();
    const patientType = patientReducer?.patientInfo?.patientType || 'OBSTETRIC'
    const inputRef = useRef(null);
    const [lmpDate, setLmpDate] = useState(null)
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const getDobFromAge = (age) => {
        const currentYear = new Date().getFullYear();
        const birthYear = currentYear - Number(age);
        return `01-01-${birthYear}`;
    };


    useEffect(() => {
        if (patientReducer?.patientInfo) {
            setMedicalDetails({
                riskLevel: patientReducer?.patientInfo.riskLevel,
                priority: patientReducer?.patientInfo.priority || 'NORMAL',
                height: patientReducer?.patientInfo.height,
                weight: patientReducer?.patientInfo.weight,
                age: patientReducer?.patientInfo.age,
            })
        }
    }, [patientReducer?.patientInfo])


    const handleChangeMedicalDetails = (field, value) => {
        setMedicalDetails(prevState => {
            const updatedData = { ...prevState };
            if (value === '') {
                delete updatedData[field];
            } else {
                updatedData[field] = value;
            }
            return updatedData;
        });
    }

    const handleUpdatePatientData = () => {
        if (medicalDetails.age) {
            const { age, weight, ...rest } = medicalDetails;
            const dob = getDobFromAge(age);
            store.dispatch(
                patientActions.updatePatientDetailsRequestAction(
                    patientId,
                    {
                        ...rest,
                        weight: weight
                            ? parseFloat(weight)
                            : undefined,
                        dateOfBirth: dob,
                    }
                )
            );
            setIsEditingMedicalInfo(false)
        } else {
            const { age, weight, ...rest } = medicalDetails;
            store.dispatch(
                patientActions.updatePatientDetailsRequestAction(
                    patientId,
                    {
                        ...rest, weight: weight
                            ? parseFloat(weight)
                            : undefined,
                    },
                )
            );
            setIsEditingMedicalInfo(false)
        }
    }

    const validateMedicalFields = (data) => {
        const { height, weight, age } = data;

        if (height == null || height === "") {
            showToast("Please enter a height between 86.36 and 332.74 cm", "error");
            return;
        }
        if (height < 86.36 || height > 332.74) {
            showToast("Please enter a height between 86.36 and 332.74 cm", "error");
            return false;
        }

        if (weight == null || weight === "") {
            showToast("Please enter a weight between 23 and 140 kg", "error");
            return;
        }

        if (!isNaN(parseFloat(weight))) {
            if (parseFloat(weight) < 23 || parseFloat(weight) > 140) {
                showToast("Please enter a weight between 23 and 140 kg", "error");
                return false;
            }
        }


        return true;
    };

    const handlePartnerChange = (field, value) => {
        setPartnerDetails(prev => ({
            ...prev,
            [field]: value,
        }));

        setPartnerChanged(true);
    };

    const getPartnerDetails = async () => {
        try {
            const res = await API.get(`/v1/patients/${patientId}/partner`);
            const partner = res.data;
            setIsEditingPartnerInfo(true)
            if (!partner) return;
            setPartnerDetails({
                name: partner.name || "",
                age: partner.age || "",
                height: partner.height || "",
                weight: partner.weight || "",
                gender: partner.gender || "",
                dateOfBirth: partner.dateOfBirth || "",
                bloodGroup: partner.bloodGroup || "",
                countryCode: partner.countryCode || "+91",
                phoneNumber: partner.phoneNumber || "",
                email: partner.email || "",
                occupation: partner.occupation || "",
                medicalDetails: partner.medicalDetails || {},
                notes: partner.notes || "",
            });
            setIsEditingPartnerInfo(false)
            setShowPartnerDetails(true);
            setIsPartnerSaved(true);
            setPartnerChanged(false);
        } catch (err) {

            console.log(err);
        }
    };

    useEffect(() => {
        if (patientId) {
            getPartnerDetails();
        }
    }, [patientId]);

    useEffect(() => {
        if (!patientId) return;
        const handleCommandOutput = (data) => {
            console.log("data came on event", data)
            const [, , rawCardType] = data?.data?.originActionType?.split('.');

            let cardType = rawCardType;
            let cardKey = rawCardType;
            if (rawCardType.includes(':')) {
                const [cardCategory, id] = rawCardType.split(':');
                cardType = cardCategory
                cardKey = rawCardType
            }
            if (consultationId !== data?.data?.consultationId) {
                return
            }
            if (cardType == "partner") {
                getPartnerDetails()
            }
        };
        socket.on("commandOutput", handleCommandOutput);
    }, [patientId]);

    const getPartnerPayload = () => ({
        relation: "SPOUSE",
        name: partnerDetails.name,
        gender: partnerDetails.gender || undefined,
        age: partnerDetails.age
            ? Number(partnerDetails.age)
            : undefined,
        height: partnerDetails.height
            ? Number(partnerDetails.height)
            : undefined,
        weight: partnerDetails.weight
            ? Number(partnerDetails.weight)
            : undefined,
    });

    const savePartnerDetails = async () => {
        try {
            const payload = getPartnerPayload();

            if (isPartnerSaved) {
                await API.put(
                    `/v1/patients/${patientId}/partner`,
                    payload
                );
            } else {
                await API.post(
                    `/v1/patients/${patientId}/partner`,
                    payload
                );
            }

            showToast("Partner details saved successfully", "success");
            setPartnerChanged(false);
            setIsPartnerSaved(true);
            getPartnerDetails();
        } catch (err) {
            console.log(err);
            showToast("Unable to save partner details", "error");
        }
    };

    const saveEdd = async () => {
        try {

            const response = await API.patch(
                `/v1/consultations/${consultationId}`,
                {
                    edd: lmpDate,
                }
            );
            if (!response.success) {
                throw new Error("")
            }
            store.dispatch(patientActions.getPatientDetailsRequestAction(patientId));
            setIsEditingPartnerInfo(false)
            showToast("EDD By USG saved successfully", "success");
        } catch (err) {
            console.log(err);
            showToast("Unable to save EDD By USG ", "error");
        }
    };

    const nameParts = partnerDetails?.name?.trim().split(/\s+/) || [];

    const avatarText =
        nameParts.length > 1
            ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
            : nameParts[0]?.[0] || "";

    const validatePartner = () => {
        const { age, height, weight } = partnerDetails;
        if (age && (Number(age) < 18 || Number(age) > 100)) {
            showToast("Please enter a valid age", "error");
            return false;
        }
        if (height && (Number(height) < 86.36 || Number(height) > 332.74)) {
            showToast("Please enter a valid height", "error");
            return false;
        }
        if (
            weight &&
            (parseFloat(weight) < 23 || parseFloat(weight) > 140)
        ) {
            showToast("Please enter a weight between 23 and 140 kg", "error");
            return false;
        }
        return true;
    };

    const renderPartnerName = () => {
        if (!isEditingPartnerInfo && partnerDetails.name && !isMobile) {
            return (
                <Tooltip title={partnerDetails.name}>
                    <Typography
                        sx={{
                            fontSize: 15,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {partnerDetails.name}
                    </Typography>
                </Tooltip>
            );
        }

        return <TextField
            size="small"
            placeholder="Partner Name"
            value={partnerDetails.name}
            onChange={(e) => handlePartnerChange("name", e.target.value)}
            sx={{
                '& .MuiOutlinedInput-root': {
                    '& input': {
                        fontSize: 13,
                        fontWeight: 700,
                        height: isMobile ? 'auto' : 20,
                        padding: isMobile ? 'auto' : '4px 8px',
                        backgroundColor: '#F2F2F2',
                        textAlign: 'left',
                        // borderRadius: 5
                    },
                },
            }}
        />
    }

    const renderPartnerAge = () => {
        return <Paper
            elevation={0}
            sx={{
                px: 0.5,
                py: 0.3,
                borderRadius: 2,
                minWidth: 30,
                textAlign: "center",
                border: "1px solid #E6E8EC",
                // bgcolor: "#FAFAFA",
            }}
        >
            {isEditingPartnerInfo ?
                <>
                    <TextField
                        style={{
                            width: '34px'
                        }}
                        value={partnerDetails.age || ''}
                        onChange={(e) => {
                            const value = e.target.value;

                            if (/^\d*$/.test(value)) {
                                handlePartnerChange("age", value);
                            }
                        }}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            maxLength: 3,
                        }}
                        placeholder="XXX"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    border: 'none',
                                },
                                '& input': {
                                    fontSize: { xl: "12px", lg: '12px', md: '10px' },
                                    height: '10px',
                                    padding: '4px 1px',
                                    backgroundColor: '#DDEEFD',
                                    textAlign: 'center',
                                    borderRadius: 5
                                },
                            },
                        }}
                    />
                    <CheckCircleOutlineIcon
                        fontSize="small"
                        sx={{ cursor: "pointer", }}
                        onClick={async () => {
                            if (validatePartner()) {
                                await savePartnerDetails();
                                setIsEditingPartnerInfo(false);
                            }

                        }}
                    />
                </>
                :
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: partnerDetails.age ? "#1F2937" : '#D3D3D3',
                    }}
                >
                    {partnerDetails.age || 'XXX'}
                </Typography>}
            <Typography
                sx={{
                    fontSize: 9,
                    color: "#6B7280",
                }}
            >
                Age (yrs)
            </Typography>
        </Paper>
    }

    const renderPartnerHeight = () => {
        return <Paper
            elevation={0}
            sx={{
                px: 0.5,
                py: 0.3,
                borderRadius: 2,
                minWidth: 50,
                textAlign: "center",
                border: "1px solid #E6E8EC",
                // bgcolor: "#FAFAFA",
            }}
        >
            {isEditingPartnerInfo ?
                <>
                    <TextField
                        style={{
                            width: '34px'
                        }}
                        value={partnerDetails.height || ''}
                        onChange={(e) => {
                            handlePartnerChange("height", e.target.value)
                        }}
                        placeholder="000"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    border: 'none',
                                },
                                '& input': {
                                    fontSize: { xl: "12px", lg: '12px', md: '10px' },
                                    height: '10px',
                                    padding: '4px 1px',
                                    backgroundColor: '#DDEEFD',
                                    textAlign: 'center',
                                    borderRadius: 5
                                },
                            },
                        }}
                    />
                    <CheckCircleOutlineIcon
                        fontSize="small"
                        sx={{ cursor: "pointer", }}
                        onClick={async () => {
                            if (validatePartner()) {
                                await savePartnerDetails();
                                setIsEditingPartnerInfo(false);
                            }

                        }}
                    />
                </>
                :
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: partnerDetails.height ? "#1F2937" : '#D3D3D3',
                    }}
                >
                    {partnerDetails.height || 'XXX'}
                </Typography>}
            <Typography
                sx={{
                    fontSize: 9,
                    color: "#6B7280",
                }}
            >
                HT (CM)
            </Typography>
        </Paper>
    }

    const renderPartnerWeight = () => {
        return <Paper
            elevation={0}
            sx={{
                px: 0.5,
                py: 0.3,
                borderRadius: 2,
                minWidth: 50,
                textAlign: "center",
                border: "1px solid #E6E8EC",
                // bgcolor: "#FAFAFA",
            }}
        >
            {isEditingPartnerInfo ?
                <>
                    <TextField
                        style={{
                            width: '34px'
                        }}
                        value={partnerDetails.weight || ''}
                        onChange={(e) => {
                            handlePartnerChange("weight", e.target.value)
                        }}
                        placeholder="00.0"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    border: 'none',
                                },
                                '& input': {
                                    fontSize: { xl: "12px", lg: '12px', md: '10px' },
                                    height: '10px',
                                    padding: '4px 1px',
                                    backgroundColor: '#DDEEFD',
                                    textAlign: 'center',
                                    borderRadius: 5
                                },
                            },
                        }}
                    />
                    <CheckCircleOutlineIcon
                        fontSize="small"
                        sx={{ cursor: "pointer", }}
                        onClick={async () => {
                            if (validatePartner()) {
                                await savePartnerDetails();
                                setIsEditingPartnerInfo(false);
                            }
                        }}
                    />
                </>
                :
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: partnerDetails.weight ? "#1F2937" : '#D3D3D3',
                    }}
                >
                    {partnerDetails.weight || 'XX.X'}
                </Typography>}
            <Typography
                sx={{
                    fontSize: 9,
                    color: "#6B7280",
                }}
            >
                WT (KG)
            </Typography>
        </Paper>
    }
    return (
        <Paper
            sx={{
                boxShadow: 'none',
                pl: 1,
            }}
        >
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <IconButton
                    onClick={() => setPatientDrawerOpen(true)}
                    aria-label="Open patient panel"
                    sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                    size="small"
                >
                    <MenuIcon />
                </IconButton>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {`${patientReducer?.patientInfo?.user?.firstName || ''} ${patientReducer?.patientInfo?.user?.lastName || ''}`.trim() || 'Patient'}
                </Typography>
            </Box>

            <Drawer
                anchor="left"
                open={patientDrawerOpen}
                onClose={() => setPatientDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: "88%",
                        maxWidth: 320,
                        height: "100dvh",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }
                }}
            >
                {/* Header */}
                <Box sx={{ p: 2, flexShrink: 0 }}>
                    <IconButton
                        onClick={() => setPatientDrawerOpen(false)}
                        sx={{ position: "absolute", top: 4, right: 4 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        px: 1,
                        WebkitOverflowScrolling: "touch",
                    }}
                >
                    <Box sx={{ border: "1px solid #E6E8EC", p: 1, mt: 1, borderRadius: 4, }} >
                        <Box sx={{ flexDirection: 'row', display: 'flex', flexWrap: 'wrap' }}>
                            <Typography
                                sx={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                }}
                            >
                                {patient?.user?.firstName} {patient?.user?.lastName}
                            </Typography>
                            {(patientReducer?.patientInfo?.age || patientReducer?.patientInfo?.height || patientReducer?.patientInfo?.weight) &&
                                <Tooltip title="Edit Details">
                                    <EditIcon onClick={() => setIsEditingMedicalInfo(!isEditingMedicalInfo)} sx={{ cursor: 'pointer', fontSize: '19px', ml: '4px' }} />
                                </Tooltip>
                            }
                        </Box>
                        <Typography
                            sx={{
                                color: "#667085",
                                fontSize: 13,
                                mt: 0.1,
                            }}
                        >
                            UHID : {patient?.patientId}
                        </Typography>

                        <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                        >
                            <Typography color="#D0D5DD">•</Typography>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: "#475467",
                                }}
                            >
                                G{patientHistory?.gravida || 0} T{patientHistory?.termCount || 0} P{patientHistory?.preTermCount || 0} A{patientHistory?.abortionCount || 0} L{patientHistory?.liveBirthCount || 0}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "repeat(2, 1fr)",
                                    sm: "repeat(3, 1fr)",
                                    md: "repeat(4, auto)",
                                },
                                gap: 1,
                                px: 1
                            }}
                        >
                            <StatCard value={medicalDetails?.age || ""} validateMedicalFields={validateMedicalFields} handleUpdatePatientData={handleUpdatePatientData} label="Age" edit={medicalDetails?.age == null || patientReducer?.patientInfo?.age == null || isEditingMedicalInfo} handleChangeMedicalDetails={handleChangeMedicalDetails} medicalDetails={medicalDetails} unit="yrs" getDobFromAge={getDobFromAge} showToast={showToast} setIsEditingMedicalInfo={setIsEditingMedicalInfo} patientId={patientId} />
                            <StatCard value={medicalDetails?.height || ''} validateMedicalFields={validateMedicalFields} handleUpdatePatientData={handleUpdatePatientData} label="HT" edit={medicalDetails?.height == null || patientReducer?.patientInfo?.height == null || isEditingMedicalInfo} handleChangeMedicalDetails={handleChangeMedicalDetails} medicalDetails={medicalDetails} unit="CM" showToast={showToast} setIsEditingMedicalInfo={setIsEditingMedicalInfo} patientId={patientId} />
                            <StatCard value={medicalDetails?.weight || ''} validateMedicalFields={validateMedicalFields} handleUpdatePatientData={handleUpdatePatientData} label="WT" edit={medicalDetails?.weight == null || patientReducer?.patientInfo?.weight == null || isEditingMedicalInfo} handleChangeMedicalDetails={handleChangeMedicalDetails} medicalDetails={medicalDetails} unit="KG" showToast={showToast} setIsEditingMedicalInfo={setIsEditingMedicalInfo} patientId={patientId} />
                            <StatCard value={(patient?.height && patient?.weight) && calculateBmi(patient?.height, patient?.weight)} label="BMI" />
                            <StatCard value={patient?.lmp ? moment(patient?.lmp).format('DD MMM YYYY') : ''} label="LMP" />
                            {patientType !== "GYNECOLOGY" && <StatCard value={patient?.edd ? moment(patient?.edd).format('DD MMM YYYY') : ''} label="EDD" />}
                            {patientType !== "GYNECOLOGY" && <StatCard value={patient?.eddByUsg ? moment(patient?.eddByUsg).format('DD MMM YYYY') : '-'} label="EDD BY USG" patient={patient} lmpDate={lmpDate} setLmpDate={setLmpDate} edit={patient?.eddByUsg == null || isEditingMedicalInfo} inputRef={inputRef} showToast={showToast} saveEdd={saveEdd} setIsEditingMedicalInfo={setIsEditingMedicalInfo}/>}
                            {patientType !== "GYNECOLOGY" && <StatWkCard value={formatWeeks(patient?.clinicalWeek) || '-'} label="wk dy" />}
                        </Box>
                    </Box>
                    <Typography sx={{ px: 2, marginTop: 1 }}>Partner Details  {!partnerChanged && <Tooltip title="Edit Partner">
                        <EditIcon
                            sx={{ cursor: "pointer", fontSize: 18 }}
                            onClick={() => {
                                setIsEditingPartnerInfo(true)
                                setPartnerChanged(true)
                            }}
                        />
                    </Tooltip>}
                        {partnerChanged && (
                            <Tooltip title="Save Partner Details">
                                <CheckCircleOutlineIcon
                                    fontSize="small"
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => {
                                        if (validatePartner()) {
                                            savePartnerDetails();
                                            setPartnerChanged(false);
                                            setIsPartnerSaved(true);
                                            setIsEditingPartnerInfo(false)
                                        }
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "repeat(2, 1fr)",
                                sm: "repeat(3, 1fr)",
                                md: "repeat(4, auto)",
                            },
                            gap: 1,
                            px: 1,
                            border: "1px solid #E6E8EC",
                            py: 2,
                            borderRadius: 4,
                        }}
                    >
                        {renderPartnerName()}
                        {renderPartnerAge()}
                        {renderPartnerHeight()}
                        {renderPartnerWeight()}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        {route == "history" && <PatientConditionTimeline patientId={patientId} />}
                        {route !== "history" && <PatientInfo patientId={patientId} />}
                    </Box>
                </Box>
            </Drawer>

            <Box alignItems="stretch" gap={1} sx={{ position: 'relative', display: { xs: 'none', md: 'flex' }, }}>
                {/* Left side: Timeline/Avatars */}
                <Box display="flex" flexDirection="column" alignItems="center" sx={{ pt: 1, pb: showPartnerDetails ? 1 : 0 }}>
                    <Avatar
                        sx={{
                            width: 34,
                            height: 34,
                            bgcolor: "#E8F5E9",
                            color: "#2E7D32",
                            fontSize: '12px'
                        }}
                    >
                        {patient?.user?.firstName?.[0]}{patient?.user?.lastName?.[0]}
                    </Avatar>

                    <Box sx={{ width: '2px', flexGrow: 1, minHeight: '20px', bgcolor: '#D0D5DD', my: 0.5 }} />

                    {isPartnerSaved ? (
                        <Avatar
                            sx={{
                                width: 34,
                                height: 34,
                                bgcolor: "#E3F2FD",
                                color: "#1976D2",
                                fontSize: '12px'
                            }}
                        >
                            {avatarText.toUpperCase()}
                        </Avatar>
                    ) : (
                        <IconButton
                            size="small"
                            onClick={() => setShowPartnerDetails(!showPartnerDetails)}
                            sx={{
                                width: 24,
                                height: 24,
                                border: '1px solid #D0D5DD',
                                bgcolor: '#FAFAFA'
                            }}
                        >
                            <Tooltip title="Add Partner Details">
                                <AddIcon sx={{ fontSize: '16px' }} />
                            </Tooltip>
                        </IconButton>
                    )}
                </Box>

                {/* Right side: Content */}
                <Box display="flex" flexDirection="column" flexGrow={1} sx={{ pt: 1 }}>
                    {/* Patient Row */}
                    <Box display="flex" alignItems="flex-start" gap={1} >
                        {/* Patient Name/Details */}
                        <Box sx={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column', width: '150px', }}>
                            <Box sx={{ flexDirection: 'row', display: 'flex' }}>
                                <Typography
                                    sx={{
                                        fontSize: 15,
                                        fontWeight: 700,
                                    }}
                                >
                                    {patient?.user?.firstName} {patient?.user?.lastName}
                                </Typography>
                                {(patientReducer?.patientInfo?.age || patientReducer?.patientInfo?.height || patientReducer?.patientInfo?.weight) &&
                                    <Tooltip title="Edit Details">
                                        <EditIcon onClick={() => setIsEditingMedicalInfo(!isEditingMedicalInfo)} sx={{ cursor: 'pointer', fontSize: '19px', ml: '4px' }} />
                                    </Tooltip>
                                }
                            </Box>

                            <Typography
                                sx={{
                                    color: "#667085",
                                    fontSize: 13,
                                    mt: 0.1,
                                }}
                            >
                                UHID : {patient?.patientId}
                            </Typography>

                            <Box
                                display="flex"
                                gap={1}
                                alignItems="center"
                            >
                                <Typography color="#D0D5DD">•</Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: "#475467",
                                    }}
                                >
                                    G{patientHistory?.gravida || 0} T{patientHistory?.termCount || 0} P{patientHistory?.preTermCount || 0} A{patientHistory?.abortionCount || 0} L{patientHistory?.liveBirthCount || 0}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Right Section */}
                        <Box display="flex" gap={1} flexWrap="wrap" >
                            <StatCard value={medicalDetails?.age || ""} validateMedicalFields={validateMedicalFields} handleUpdatePatientData={handleUpdatePatientData} label="Age" edit={medicalDetails?.age == null || patientReducer?.patientInfo?.age == null || isEditingMedicalInfo} handleChangeMedicalDetails={handleChangeMedicalDetails} medicalDetails={medicalDetails} unit="yrs" getDobFromAge={getDobFromAge} showToast={showToast} setIsEditingMedicalInfo={setIsEditingMedicalInfo} patientId={patientId} />
                            <StatCard value={medicalDetails?.height || ''} validateMedicalFields={validateMedicalFields} handleUpdatePatientData={handleUpdatePatientData} label="HT" edit={medicalDetails?.height == null || patientReducer?.patientInfo?.height == null || isEditingMedicalInfo} handleChangeMedicalDetails={handleChangeMedicalDetails} medicalDetails={medicalDetails} unit="CM" showToast={showToast} setIsEditingMedicalInfo={setIsEditingMedicalInfo} patientId={patientId} />
                            <StatCard value={medicalDetails?.weight || ''} validateMedicalFields={validateMedicalFields} handleUpdatePatientData={handleUpdatePatientData} label="WT" edit={medicalDetails?.weight == null || patientReducer?.patientInfo?.weight == null || isEditingMedicalInfo} handleChangeMedicalDetails={handleChangeMedicalDetails} medicalDetails={medicalDetails} unit="KG" showToast={showToast} setIsEditingMedicalInfo={setIsEditingMedicalInfo} patientId={patientId} />
                            <StatCard value={(patient?.height && patient?.weight) && calculateBmi(patient?.height, patient?.weight)} label="BMI" />
                        </Box>
                        <Box
                            display="flex"
                            gap={1}
                            flexWrap="wrap"
                        >
                            <StatCard value={patient?.lmp ? moment(patient?.lmp).format('DD MMM YYYY') : '-'} label="LMP" />
                            {patientType !== "GYNECOLOGY" && <StatCard value={patient?.edd ? moment(patient?.edd).format('DD MMM YYYY') : '-'} label="EDD" />}
                            {patientType !== "GYNECOLOGY" && <StatCard value={patient?.eddByUsg ? moment(patient?.eddByUsg).format('DD MMM YYYY') : '-'} label="EDD BY USG" patient={patient} lmpDate={lmpDate} setLmpDate={setLmpDate} edit={patient?.eddByUsg == null || isEditingMedicalInfo} inputRef={inputRef} showToast={showToast} saveEdd={saveEdd} setIsEditingMedicalInfo={setIsEditingMedicalInfo} />}
                            {patientType !== "GYNECOLOGY" && <StatWkCard value={formatWeeks(patient?.clinicalWeek) || '-'}label="wk dy" />}
                        </Box>
                    </Box>

                    {/* Partner Row */}
                    {showPartnerDetails && (
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 'auto', }}>
                            <Box sx={{ width: '150px', display: 'flex', alignItems: 'center', gap: 1 }}>

                                {renderPartnerName()}
                                {!partnerChanged && <Tooltip title="Edit Partner">
                                    <EditIcon
                                        sx={{ cursor: "pointer", fontSize: 18 }}
                                        onClick={() => {
                                            setIsEditingPartnerInfo(true)
                                            setPartnerChanged(true)
                                        }}
                                    />
                                </Tooltip>}
                                {partnerChanged && (
                                    <Tooltip title="Save Partner Details">
                                        <CheckCircleOutlineIcon
                                            fontSize="small"
                                            sx={{ cursor: "pointer", }}
                                            onClick={() => {
                                                if (validatePartner()) {
                                                    savePartnerDetails();
                                                    setPartnerChanged(false);
                                                    setIsPartnerSaved(true);
                                                    setIsEditingPartnerInfo(false)
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                {renderPartnerAge()}
                                {renderPartnerHeight()}
                                {renderPartnerWeight()}
                                {/* <StatCard value="27.0" label="BMI" /> */}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
    );
}